import { Add, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import { SetStateAction, useEffect, useState } from 'react';
import { useAlert } from '../../context/AlertContext';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import { useGetRelationshipTypeHasRelationshipRole } from '../../hooks/data';
import {
  useGetRelationshipRoles,
  useGetRelationshipTypes,
} from '../../hooks/reactQuerys/RelationshipQueries';
import { ILegalClient, initLegalClient } from '../../Interfaces/ILegalClient';
import {
  ILegalClientRelationship,
  initLegalClientRelationship,
  issueMembers,
} from '../../Interfaces/ILegalClientRelationship/ILegalClientRelationship';
import { IRelationshipRole } from '../../Interfaces/IRelationshipRole';
import { IRelationshipType } from '../../Interfaces/IRelationshipType';
import { IRelationshipTypeHasRelationshipRole } from '../../Interfaces/IRelationshipTypeHasRelationshipRole';
import { LegalClientSelectDialog } from '../LegalClientSelectDialog';

interface IProps {
  editedLegalClientId?: number;
  selectedRelationship?: ILegalClientRelationship;
  legalClients: ILegalClient[];
  relationships: ILegalClientRelationship[];
  setRelationships: React.Dispatch<SetStateAction<ILegalClientRelationship[]>>;
  unmount: () => void;
}

const defaultRequiredMember = {
  ...initLegalClient(),
  FirstName: 'Neuer',
  LastName: 'Mandant',
};

const issuePossibleRoles = (
  relationshipTypeId: number,
  relationshipRoles: IRelationshipRole[],
  relationshipTypeHasRelationshipRoleArray: IRelationshipTypeHasRelationshipRole[]
) =>
  relationshipRoles.filter(
    relationship =>
      relationshipTypeHasRelationshipRoleArray.find(
        hasRelationshipRole =>
          hasRelationshipRole.idRelationshipType === relationshipTypeId &&
          hasRelationshipRole.idRelationshipRole ===
            relationship.idRelationshipRole
      ) !== undefined
  );

const getRelationshipDefaults = (
  legalClients: ILegalClient[],
  relationshipTypes: IRelationshipType[] | undefined,
  possibleRelationshipRoles: IRelationshipRole[] | undefined
) => {
  if (
    !relationshipTypes ||
    relationshipTypes.length === 0 ||
    !possibleRelationshipRoles ||
    possibleRelationshipRoles.length === 0
  ) {
    return {
      hasDefaults: false,
    } as const;
  }

  const defaultRelationshipType = relationshipTypes[0];
  const defaultRelationshipRole = possibleRelationshipRoles[0];

  return {
    hasDefaults: true,
    defaultRelationshipTypeId: defaultRelationshipType.idRelationshipType,
    defaultRelationshipType: defaultRelationshipType.RelationshipType,
    defaultRelationshipRoleId: defaultRelationshipRole.idRelationshipRole,
    defaultRelationshipRole: defaultRelationshipRole.RelationshipRole,
  } as const;
};

export const UpdateOrCreateRelationshipDialog = ({
  editedLegalClientId,
  selectedRelationship,
  legalClients,
  relationships,
  setRelationships,
  unmount,
}: IProps) => {
  const [isSelectDialogMount, setIsSelectDialogMount] = useState(false);

  const { relationshipTypes } = useGetRelationshipTypes();
  const { relationshipRoles } = useGetRelationshipRoles();
  const { relationshipTypeHasRelationshipRole } =
    useGetRelationshipTypeHasRelationshipRole();

  const [currentRelationship, setCurrentRelationship] = useState<
    ILegalClientRelationship | undefined
  >(selectedRelationship);
  const [possibleRoles, setPossibleRoles] = useState<
    IRelationshipRole[] | undefined
  >(undefined);

  const { showAlert } = useAlert();

  const defaults = getRelationshipDefaults(
    legalClients,
    relationshipTypes,
    possibleRoles
  );

  useEffect(() => {
    if (
      !selectedRelationship &&
      relationshipTypes &&
      relationshipRoles &&
      defaults.hasDefaults
    ) {
      setCurrentRelationship(
        initLegalClientRelationship(
          legalClients.find(lc => lc.idLegalClient === editedLegalClientId) ??
            defaultRequiredMember,
          defaults.defaultRelationshipTypeId,
          defaults.defaultRelationshipType,
          defaults.defaultRelationshipRoleId,
          defaults.defaultRelationshipRole
        )
      );
    }
  }, [
    selectedRelationship,
    editedLegalClientId,
    relationshipRoles,
    relationshipTypes,
    defaults.hasDefaults,
  ]);

  useEffect(() => {
    if (
      relationshipRoles &&
      relationshipTypeHasRelationshipRole &&
      currentRelationship
    ) {
      setPossibleRoles(
        issuePossibleRoles(
          currentRelationship.idRelationshipType,
          relationshipRoles,
          relationshipTypeHasRelationshipRole
        )
      );
    }
  }, [
    currentRelationship,
    relationshipRoles,
    relationshipTypeHasRelationshipRole,
    setPossibleRoles,
  ]);

  if (
    !relationshipTypes ||
    !relationshipRoles ||
    !relationshipTypeHasRelationshipRole ||
    !currentRelationship ||
    !possibleRoles ||
    !defaults.hasDefaults
  ) {
    return <LoadingSpinner />;
  }

  const onSubmit = () => {
    try {
      if (!!selectedRelationship) {
        setRelationships(
          relationships.map(r =>
            r.idRelationship === currentRelationship.idRelationship
              ? currentRelationship
              : r
          )
        );
      } else {
        setRelationships([...relationships, currentRelationship]);
      }

      unmount();
    } catch (error) {
      console.error(error);
      showAlert({
        text: 'Die Beziehung konnte nicht hinzugefügt werden!',
        severity: 'error',
      });
    }
  };

  const onSelect = (selectedLegalClients: ILegalClient[]) => {
    const oldMembers = currentRelationship.Members;

    const { idRelationshipRole, RelationshipRole } = issuePossibleRoles(
      currentRelationship.idRelationshipType,
      relationshipRoles,
      relationshipTypeHasRelationshipRole
    )[0];

    const newMembers = issueMembers(
      selectedLegalClients.filter(
        lc => !oldMembers.some(m => m.idLegalClient === lc.idLegalClient)
      ),
      idRelationshipRole,
      RelationshipRole
    );

    setCurrentRelationship({
      ...currentRelationship,
      Members: [...oldMembers, ...newMembers],
    });
  };

  const onRemove = (index: number) =>
    setCurrentRelationship({
      ...currentRelationship,
      Members: [...currentRelationship.Members.filter((_, mi) => mi !== index)],
    });

  return (
    <>
      {isSelectDialogMount && (
        <LegalClientSelectDialog
          blockedIds={editedLegalClientId ? [editedLegalClientId] : []}
          selectedIds={currentRelationship.Members.map(x => x.idLegalClient)}
          setSelected={onSelect}
          allLegalClients={legalClients}
          unmount={() => setIsSelectDialogMount(false)}
        />
      )}

      <Dialog onClose={unmount} open={true} fullWidth maxWidth='sm'>
        <DialogTitle>
          {!!selectedRelationship
            ? 'Beziehung bearbeiten'
            : 'Beziehung hinzufügen'}
        </DialogTitle>
        <DialogContent sx={{ minHeight: '40vh' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              required
              label='Relationstyp'
              select
              fullWidth
              size='small'
              sx={{ mt: 1 }}
              value={currentRelationship.idRelationshipType}
              onChange={({ target }) => {
                const newRelationship = editedLegalClientId
                  ? currentRelationship
                  : initLegalClientRelationship(
                      legalClients.find(
                        lc => lc.idLegalClient === editedLegalClientId
                      ) ?? defaultRequiredMember,
                      defaults.defaultRelationshipTypeId,
                      defaults.defaultRelationshipType,
                      defaults.defaultRelationshipRoleId,
                      defaults.defaultRelationshipRole
                    );

                const newRelationshipTypeId = Number(target.value);
                const newRelationshipType = relationshipTypes.find(
                  t => t.idRelationshipType === newRelationshipTypeId
                )!.RelationshipType;

                newRelationship.idRelationshipType = newRelationshipTypeId;
                newRelationship.RelationshipType = newRelationshipType;

                const { RelationshipRole, idRelationshipRole } =
                  issuePossibleRoles(
                    newRelationship.idRelationshipType,
                    relationshipRoles,
                    relationshipTypeHasRelationshipRole
                  )[0];

                setCurrentRelationship({
                  ...newRelationship,
                  Members: newRelationship.Members.map(m => ({
                    ...m,
                    idRelationshipRole: idRelationshipRole,
                    RelationshipRole: RelationshipRole,
                  })),
                });
              }}
            >
              {relationshipTypes.map((type, i) => (
                <MenuItem key={i} value={type.idRelationshipType}>
                  {type.RelationshipType}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box my={2}>
            <Typography variant='h6' mb={1}>
              Personen
              <IconButton
                sx={{ float: 'right' }}
                onClick={() => setIsSelectDialogMount(true)}
              >
                <Add />
              </IconButton>
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={200}>Rolle</TableCell>
                    <TableCell>Person</TableCell>
                    <TableCell width={90}>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentRelationship.Members.map((m, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <TextField
                          label='Rolle'
                          select
                          fullWidth
                          size='small'
                          value={m.idRelationshipRole}
                          onChange={e => {
                            const selectedRoleId = Number(e.target.value);
                            const selectedRole = relationshipRoles.find(
                              r => r.idRelationshipRole === selectedRoleId
                            );

                            const newMembers = _.cloneDeep(
                              currentRelationship.Members
                            );

                            newMembers[i] = {
                              ...newMembers[i],
                              idRelationshipRole:
                                selectedRole!.idRelationshipRole,
                              RelationshipRole: selectedRole!.RelationshipRole,
                            };

                            setCurrentRelationship({
                              ...currentRelationship,
                              Members: newMembers,
                            });
                          }}
                        >
                          {possibleRoles.map((r, i) => (
                            <MenuItem key={i} value={r.idRelationshipRole}>
                              {r.RelationshipRole}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        {m.LegalClient?.FirstName} {m.LegalClient?.LastName}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size='small'
                          disabled={
                            editedLegalClientId
                              ? m.idLegalClient === editedLegalClientId
                              : m.idLegalClient === -1
                          }
                          onClick={() => onRemove(i)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => unmount()}>
            Abbruch
          </Button>
          <Button
            variant='contained'
            onClick={onSubmit}
            disabled={currentRelationship.Members.length === 0}
          >
            {!!selectedRelationship ? 'Speichern' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
